import { Fragment, useCallback } from "react";

import { Element } from "@craftjs/core";
import { RefreshCcwIcon } from "lucide-react";
import { Button, DefaultTooltip } from "twyne-new.ui";

import { define, prop, usePureCraftProp } from "@/components/builder/utils";
import { FormProvider } from "@/modules/offer/views/form/store";

import { FormContainerBlock } from "..";
import SelectAsyncProperty from "../../properties/SelectAsync";
import BasePreview from "../base";

import { useFormLoader, useFormOptions, useFormProperties, useStepOptions } from "./context";

export function FormLoaderPreview({ properties: { formId, step } = {} }) {
  useFormLoader({ formId, step })

  if (!formId) {
    return <BasePreview>
      <div>Select a form</div>
    </BasePreview>
  }

  return <BasePreview>
    <div>
      <Element key={`form-container-${formId}`} id={`form-container-${formId}`} is={FormContainerBlock} />
    </div>
  </BasePreview>
}

function ContextWrapper({ children }) {
  useFormProperties()
  return children
}

function PropertiesContext({ children }) {
  return <FormProvider>
    <ContextWrapper>
      {children}
    </ContextWrapper>
  </FormProvider>
}

function SelectFormProperty(props) {
  const formId = usePureCraftProp('properties.formId')

  const handleRefresh = useCallback(() => {
    const { value } = formId
    formId.setValue(null)
    setTimeout(() => {
      formId.setValue(value)
    }, 100)
  }, [formId])

  return <div className="flex flex-row gap-2">
    <SelectAsyncProperty {...props} />
    <Button variant="outline" color="accent" size="sm" onClick={handleRefresh}>
      <DefaultTooltip content="Re-sync selected form" side="right">
        <RefreshCcwIcon size={16} />
      </DefaultTooltip>
    </Button>
  </div>
}

export default define('form-loader', FormLoaderPreview, Fragment)
  .context(PropertiesContext)
  .property(prop.select.async('Form', 'formId').category('default').ignoreDevice(true).own()
    .component(SelectFormProperty)
    .props({
      useOptions: useFormOptions,
      clearable: true,
    }))
  .property(prop.select.async('Step', 'step').category('default').blockClear().ignoreDevice(true).own()
    .props({
      useOptions: useStepOptions,
    }))
  .defaultProps({
    step: '1',
  })
  .build()
